import { useApp } from '../context.js';
import { isAmount } from '../lib/money.js';
import { DollarSign, Trophy } from '../icons.jsx';

export default function CheckGame({ restaurant }) {
    const { activeGame, actualAmount, backToGuessing, closeGame, confirmAndSave, currentProfile, myGuess, resultCountdown, setActualAmount, setMyGuess, startCheckGame, submitActual, submitMyGuess, t } = useApp();
    return (
        <>
            {/* Start Game Button - only show if no active game for this restaurant */}
            {(!activeGame || activeGame.restaurantId !== restaurant.id) && (
                <button
                    onClick={() => startCheckGame(restaurant.id)}
                    disabled={activeGame && activeGame.restaurantId !== restaurant.id}
                    className="w-full bg-gradient-to-r from-green-400 to-emerald-500 text-white px-4 py-3 rounded-xl font-semibold apple-button flex items-center justify-center gap-2 disabled:from-gray-300 disabled:to-gray-300"
                >
                    <DollarSign size={20} />
                    {activeGame ? 'Game in progress...' : 'Guess the Check!'}
                </button>
            )}

            {/* Active Game UI */}
            {activeGame && activeGame.restaurantId === restaurant.id && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl">
                    {/* Guessing Phase */}
                    {activeGame.status === 'guessing' && (
                        <div className="space-y-3">
                            <h4 className="text-base font-bold text-green-700 text-center">
                                💰 Guess the Check! 💰
                            </h4>

                            {/* Show my guess input if I haven't guessed */}
                            {!(activeGame.guesses && isAmount(activeGame.guesses[currentProfile])) ? (
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Your Guess ({currentProfile === 'doug' ? 'Doug' : 'Les'})
                                    </label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        value={myGuess}
                                        onChange={(e) => setMyGuess(e.target.value)}
                                        placeholder="$0.00"
                                        className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:outline-none focus:border-green-400 text-base"
                                    />
                                    <button
                                        onClick={submitMyGuess}
                                        disabled={!myGuess}
                                        className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold apple-button disabled:bg-gray-300"
                                    >
                                        Lock In My Guess
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-white p-3 rounded-xl text-center">
                                    <p className="text-sm text-gray-600">Your guess locked: <span className="font-bold">${(activeGame.guesses?.[currentProfile] || 0).toFixed(2)}</span></p>
                                </div>
                            )}

                            {/* Status of both players */}
                            <div className="bg-white p-3 rounded-xl space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-blue-600">Doug</span>
                                    {isAmount(activeGame.guesses?.doug)
                                        ? <span className="text-green-500 text-sm">✓ Locked</span>
                                        : <span className={`${t.accentText} text-sm animate-pulse`}>Waiting...</span>
                                    }
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-pink-500">Les</span>
                                    {isAmount(activeGame.guesses?.les)
                                        ? <span className="text-green-500 text-sm">✓ Locked</span>
                                        : <span className={`${t.accentText} text-sm animate-pulse`}>Waiting...</span>
                                    }
                                </div>
                            </div>

                            {/* Doug can enter actual once both have guessed */}
                            {isAmount(activeGame.guesses?.doug) && isAmount(activeGame.guesses?.les) && currentProfile === 'doug' && (
                                <div className="space-y-2 border-t border-green-200 pt-3">
                                    <label className="block text-sm font-semibold text-gray-700">Actual Amount (Doug enters)</label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        value={actualAmount}
                                        onChange={(e) => setActualAmount(e.target.value)}
                                        placeholder="$0.00"
                                        className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:outline-none focus:border-green-400 text-base"
                                    />
                                    <button
                                        onClick={submitActual}
                                        disabled={!actualAmount}
                                        className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold apple-button disabled:bg-gray-300"
                                    >
                                        Continue to Confirm
                                    </button>
                                </div>
                            )}

                            {/* Les waiting for Doug to enter actual */}
                            {isAmount(activeGame.guesses?.doug) && isAmount(activeGame.guesses?.les) && currentProfile === 'les' && (
                                <div className="text-center text-sm text-gray-600 py-2">
                                    Waiting for Doug to enter the actual amount...
                                </div>
                            )}

                            <button
                                onClick={closeGame}
                                className="w-full bg-gray-200 text-gray-600 py-2 rounded-xl text-sm apple-button"
                            >
                                Cancel Game
                            </button>
                        </div>
                    )}

                    {/* Confirm Phase */}
                    {activeGame.status === 'confirm' && (
                        <div className="space-y-3">
                            <h4 className="text-base font-bold text-green-700 text-center">
                                Confirm Details
                            </h4>
                            <div className="bg-white p-4 rounded-xl space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Doug's guess:</span>
                                    <span className="font-bold text-blue-600">${(activeGame.guesses?.doug || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Les's guess:</span>
                                    <span className="font-bold text-pink-500">${(activeGame.guesses?.les || 0).toFixed(2)}</span>
                                </div>
                                <div className="border-t border-gray-200 pt-2 mt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Actual check:</span>
                                        <span className="font-bold text-green-600 text-lg">${(activeGame.actual || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                            {currentProfile === 'doug' ? (
                                <div className="flex gap-2">
                                    <button
                                        onClick={backToGuessing}
                                        className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold apple-button"
                                    >
                                        ← Edit
                                    </button>
                                    <button
                                        onClick={confirmAndSave}
                                        className="flex-1 bg-green-600 text-white py-3 rounded-xl font-semibold apple-button"
                                    >
                                        Reveal Winner!
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center text-sm text-gray-600 py-2">
                                    Waiting for Doug to confirm...
                                </div>
                            )}
                        </div>
                    )}

                    {/* Complete Phase - Results */}
                    {activeGame.status === 'complete' && (() => {
                        const dougGuess = activeGame.guesses?.doug || 0;
                        const lesGuess = activeGame.guesses?.les || 0;
                        const actual = activeGame.actual || 0;
                        const dougDiff = Math.abs(dougGuess - actual);
                        const lesDiff = Math.abs(lesGuess - actual);
                        const winner = activeGame.winner;
                        return (
                            <div className="space-y-3">
                                <div className="text-center">
                                    <Trophy size={40} className="mx-auto text-yellow-500 animate-bounce" />
                                    <h4 className="text-xl font-bold text-green-700 mt-2">
                                        {winner === 'doug' ? '🎉 Doug Wins!' : winner === 'les' ? '🎉 Les Wins!' : "🤝 It's a Tie!"}
                                    </h4>
                                </div>
                                <div className="bg-white p-3 rounded-xl space-y-2">
                                    <div className={`flex justify-between items-center p-2 rounded-lg ${winner === 'doug' ? 'bg-blue-50' : ''}`}>
                                        <span className="font-semibold text-blue-600">Doug</span>
                                        <div className="text-right">
                                            <span className="font-bold text-gray-800">${dougGuess.toFixed(2)}</span>
                                            <span className="text-sm text-gray-500 ml-2">±${dougDiff.toFixed(2)}</span>
                                            {winner === 'doug' && <span className="ml-1">🏆</span>}
                                        </div>
                                    </div>
                                    <div className={`flex justify-between items-center p-2 rounded-lg ${winner === 'les' ? 'bg-pink-50' : ''}`}>
                                        <span className="font-semibold text-pink-500">Les</span>
                                        <div className="text-right">
                                            <span className="font-bold text-gray-800">${lesGuess.toFixed(2)}</span>
                                            <span className="text-sm text-gray-500 ml-2">±${lesDiff.toFixed(2)}</span>
                                            {winner === 'les' && <span className="ml-1">🏆</span>}
                                        </div>
                                    </div>
                                    <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Actual</span>
                                        <span className="font-bold text-green-600">${actual.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="text-center text-sm text-gray-500">
                                    {resultCountdown !== null && resultCountdown > 0
                                        ? `Closing in ${resultCountdown}...`
                                        : 'Saved!'}
                                </div>
                                <button
                                    onClick={closeGame}
                                    className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold apple-button"
                                >
                                    Done
                                </button>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Recent Checks */}
            {restaurant.checks && restaurant.checks.length > 0 && (
                <div className="mt-3">
                    <h5 className="text-xs font-bold text-gray-600 mb-2">Recent Checks</h5>
                    <div className="space-y-2">
                        {restaurant.checks.slice(-2).reverse().map((check) => (
                            <div key={check.id} className="bg-gray-50 p-2 rounded-lg text-xs">
                                <div className="flex justify-between mb-1">
                                    <span className="font-medium text-blue-600">Doug: ${Number(check.dougGuess || 0).toFixed(2)}</span>
                                    <span className="font-medium text-pink-500">Les: ${Number(check.lesGuess || 0).toFixed(2)}</span>
                                </div>
                                <div className="text-center font-bold text-green-600">
                                    ${Number(check.actualAmount || 0).toFixed(2)} • {check.winner === 'doug' ? '💛 Doug' : check.winner === 'les' ? '💖 Les' : '🤝 Tie'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
